<template>
  <div>
    <div style="max-width: 525px" v-if="verificationStatus != 1">
      <v-alert :type="alertType" dense text border="left">
        {{ alertMessage }}
      </v-alert>
    </div>

    <div style="max-width: 764px" v-if="verificationStatus == '4'">
      <p class="my-2">
        {{ $t("verification.document.denied.someMotives") }}
      </p>
      <ul>
        <li>
          {{ $t("verification.document.denied.motive1") }}
        </li>
        <li>
          {{ $t("verification.document.denied.motive2") }}
        </li>
        <li>
          {{ $t("verification.document.denied.motive3") }}
        </li>
        <li>
          {{ $t("verification.document.denied.motive4") }}
        </li>
      </ul>
      <p class="my-2">
        {{ $t("verification.document.denied.doubts") }}
      </p>
    </div>
    <div v-else-if="verificationStatus == '1'">
      <div style="max-width: 764px" class="mb-8">
        <p>
          {{ $t("verification.document.description") }}
        </p>

        <span class="text-subtitle-1">
          {{ $t("verification.document.acceptedDocuments") }}
        </span>
        <ul>
          <li>
            {{ $t("verification.document.possibleDocuments.passport") }}
          </li>
          <li>
            {{ $t("verification.document.possibleDocuments.driversLicense") }}
          </li>
          <li>
            {{ $t("verification.document.possibleDocuments.identity") }}
          </li>
          <li>
            {{ $t("verification.document.possibleDocuments.workCard") }}
          </li>
        </ul>

        <p class="mt-4">
          {{ $t("verification.document.filesDetail") }}
        </p>
      </div>

      <v-form v-model="documentVerificationForm" @submit="validateIdentity">
        <div class="my-4" style="max-width: 525px">
          <div
            class="d-flex justify-between"
            :class="$vuetify.breakpoint.mobile ? 'flex-column' : ''"
          >
            <div class="d-flex flex-column">
              <div class="mb-1">
                {{ $t("verification.document.inputs.documentFront") }}
                <span class="red--text">*</span>
              </div>

              <!-- DOCUMENT FRONT INPUT -->
              <v-file-input
                ref="documentFront"
                v-model="images.documentFront"
                :label="
                  images.documentFront == null ||
                  images.documentFront.length === 0
                    ? $t('verification.document.nothingSelected')
                    : ''
                "
                outlined
                append-icon="mdi-tray-arrow-up"
                @click:append="openFileSelecter('documentFront')"
                prepend-icon=""
                show-size
                :hint="allowedImageTypesHint"
                persistent-hint
                :error="errors.documentFront != ''"
                :error-messages="errors.documentFront"
                style="width: 525px"
              >
              </v-file-input>
            </div>

            <!-- previewImages.documentFront -->
            <div
              v-if="previewImages.documentFront.src !== ''"
              class="d-flex"
              :class="$vuetify.breakpoint.mobile ? 'justify-center' : ''"
            >
              <v-fade-transition>
                <v-card
                  outlined
                  width="150"
                  height="100"
                  :class="$vuetify.breakpoint.mobile ? 'my-2' : 'ml-4'"
                  :style="
                    errors.documentFront ===
                    $t('verification.document.documentNotVertical')
                      ? 'overflow: auto;'
                      : 'overflow: hidden;'
                  "
                >
                  <div v-if="previewImages.documentFront.type.includes('pdf')">
                    <pdf
                      ref="previewPdfdocumentFront"
                      :src="previewImages.documentFront.src"
                      v-for="i in [1]"
                      :key="i + updatePdfKey.documentFront"
                      :id="i"
                      :page="1"
                      style="max-width: 100%; max-height: 100%"
                    />
                  </div>

                  <v-img
                    v-else
                    contain
                    :src="previewImages.documentFront.src"
                    width="100%"
                    height="100%"
                  />
                </v-card>
              </v-fade-transition>

              <v-icon
                class="ml-2"
                size="16"
                :color="errors.documentFront === '' ? 'success' : 'error'"
                v-if="images.documentFront && isFile(images.documentFront)"
              >
                {{
                  errors.documentFront === ""
                    ? "mdi-check-circle"
                    : "mdi-alert-circle"
                }}
              </v-icon>
            </div>
          </div>

          <div
            class="d-flex justify-between mt-4"
            :class="$vuetify.breakpoint.mobile ? 'flex-column' : ''"
          >
            <div class="d-flex flex-column" style="max-width: 100%">
              <div class="mb-1">
                {{ $t("verification.document.inputs.documentVerse") }}
                <span class="red--text">*</span>
              </div>

              <!-- DOCUMENT VERSE INPUT -->
              <v-file-input
                ref="documentVerse"
                v-model="images.documentVerse"
                :label="
                  images.documentVerse == null ||
                  images.documentVerse.length === 0
                    ? $t('verification.document.nothingSelected')
                    : ''
                "
                outlined
                append-icon="mdi-tray-arrow-up"
                @click:append="openFileSelecter('documentVerse')"
                prepend-icon=""
                show-size
                :hint="allowedImageTypesHint"
                persistent-hint
                :error="errors.documentVerse != ''"
                :error-messages="errors.documentVerse"
                style="width: 525px"
                :disabled="
                  images.documentFront == null || errors.documentFront != ''
                "
              ></v-file-input>
            </div>

            <!-- previewImages.documentVerse -->
            <div
              v-if="previewImages.documentVerse.src !== ''"
              class="d-flex"
              :class="$vuetify.breakpoint.mobile ? 'justify-center' : ''"
            >
              <v-fade-transition>
                <v-card
                  outlined
                  width="150"
                  height="100"
                  :class="$vuetify.breakpoint.mobile ? 'my-2' : 'ml-4'"
                  :style="
                    errors.documentVerse ===
                    $t('verification.document.documentNotVertical')
                      ? 'overflow: auto;'
                      : 'overflow: hidden;'
                  "
                >
                  <div v-if="previewImages.documentVerse.type.includes('pdf')">
                    <pdf
                      ref="previewPdfdocumentVerse"
                      :src="previewImages.documentVerse.src"
                      v-for="i in [2]"
                      :key="i + updatePdfKey.documentVerse"
                      :id="i"
                      :page="1"
                      style="max-width: 100%; max-height: 100%"
                    />
                  </div>

                  <v-img
                    v-else
                    contain
                    :src="previewImages.documentVerse.src"
                    width="100%"
                    height="100%"
                  />
                </v-card>
              </v-fade-transition>

              <v-icon
                class="ml-2"
                size="16"
                :color="errors.documentVerse === '' ? 'success' : 'error'"
                v-if="images.documentVerse && isFile(images.documentVerse)"
              >
                {{
                  errors.documentVerse === ""
                    ? "mdi-check-circle"
                    : "mdi-alert-circle"
                }}
              </v-icon>
            </div>
          </div>

          <div
            class="d-flex justify-between mt-2"
            :class="$vuetify.breakpoint.mobile ? 'flex-column' : ''"
          >
            <div class="d-flex flex-column" style="max-width: 100%">
              <div class="mb-1">
                {{ $t("verification.document.inputs.faceBiometry") }}
                <span class="red--text">*</span>
              </div>

              <!-- DOCUMENT FACE BIOMETRY INPUT -->
              <v-file-input
                ref="faceBiometry"
                v-model="images.faceBiometry"
                :label="
                  images.faceBiometry == null ||
                  images.faceBiometry.length === 0
                    ? $t('verification.document.nothingSelected')
                    : ''
                "
                outlined
                append-icon="mdi-tray-arrow-up"
                @click:append="openFileSelecter('faceBiometry')"
                prepend-icon=""
                show-size
                :hint="allowedImageTypesHint"
                persistent-hint
                :error="errors.faceBiometry != ''"
                :error-messages="errors.faceBiometry"
                style="width: 525px"
                :disabled="
                  images.documentFront == null ||
                    images.documentVerse == null ||
                    errors.documentVerse != ''
                "
              ></v-file-input>
            </div>

            <div
              v-if="previewImages.faceBiometry.src !== ''"
              class="d-flex justify-center"
            >
              <v-fade-transition>
                <v-card
                  outlined
                  width="150"
                  :height="$vuetify.breakpoint.mobile ? 200 : 150"
                  :class="$vuetify.breakpoint.mobile ? 'my-2' : 'ml-4'"
                  style="overflow: auto"
                >
                  <div
                    v-if="previewImages.faceBiometry.type.includes('pdf')"
                    class="mx-auto"
                  >
                    <pdf
                      :src="previewImages.faceBiometry.src"
                      v-for="i in [3]"
                      :key="i + updatePdfKey.faceBiometry"
                      :id="i"
                      :page="1"
                      style="width: 100%; margin: 0 auto"
                    />
                  </div>

                  <v-img
                    v-else
                    contain
                    :src="previewImages.faceBiometry.src"
                    width="100%"
                    height="100%"
                  />
                </v-card>
              </v-fade-transition>

              <v-icon
                class="ml-2"
                size="16"
                :color="errors.faceBiometry === '' ? 'success' : 'error'"
                v-if="images.faceBiometry && isFile(images.faceBiometry)"
              >
                {{
                  errors.faceBiometry === ""
                    ? "mdi-check-circle"
                    : "mdi-alert-circle"
                }}
              </v-icon>
            </div>
          </div>
        </div>

        <div class="d-flex align-center my-4">
          <v-icon color="primary" small> mdi-information-outline </v-icon>
          <div
            class="ml-2 text-body-2"
            style="width: 500px"
            v-html="$t('verification.document.alert')"
          ></div>
        </div>
        <v-btn
          type="submit"
          class="primary"
          @click.prevent="validateIdentity"
          :disabled="!documentVerificationForm || bankValidationLoading"
          :loading="bankValidationLoading"
        >
          {{ $t("btnSend") }}
        </v-btn>
      </v-form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import pdf from "pdfvuer";

const allowedMimes = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "application/pdf",
];

export default {
  props: ["userCpf", "verificationStatus"],
  data: () => ({
    documentVerificationForm: false,

    updatePdfKey: {
      documentFront: 0,
      documentVerse: 0,
      faceBiometry: 0,
    },

    images: {
      documentFront: null,
      documentVerse: null,
      faceBiometry: null,
    },

    previewImages: {
      documentFront: {
        src: "",
        type: "",
      },
      documentVerse: {
        src: "",
        type: "",
      },
      faceBiometry: {
        src: "",
        type: "",
      },
    },

    errors: {
      documentFront: "",
      documentVerse: "",
      faceBiometry: "",
    },

    bankValidationLoading: false,
  }),

  components: {
    pdf,
  },

  created() {
    // this.errors.documentFront = this.$t("required");
    // this.errors.documentVerse = this.$t("required");
    // this.errors.faceBiometry = this.$t("required");
  },

  computed: {
    allowedImageTypesHint() {
      return this.$t("verification.document.allowedTypes");
    },
    alertMessage() {
      const map = {
        2: this.$t("verification.document.alertMessage.inProgress"),
        3: this.$t("verification.document.alertMessage.finished"),
        4: this.$t("verification.document.alertMessage.denied"),
      };

      return map[this.verificationStatus];
    },
    alertType() {
      const map = {
        2: "info",
        3: "success",
        4: "error",
      };

      return map[this.verificationStatus];
    },
  },

  watch: {
    "images.documentFront"(val) {
      this.validateDocument(val, "documentFront");
    },
    "images.documentVerse"(val) {
      if (val != null) {
        // Verifica se a primeira imagem Front é Jpeg e a Segunda é PDF
        // Fix evita um erro no lado servidor
        var typeFront = this.previewImages.documentFront.type;
        var isFrontImg =
          typeFront === "image/jpg" ||
          typeFront === "image/jpeg" ||
          typeFront === "image/png";
        var isVersePDF = val.type === "application/pdf";
        if (isFrontImg && isVersePDF) {
          this.errors.documentVerse = this.$t(
            "verification.document.fileImgSame"
          );
          return;
        }
      }

      this.validateDocument(val, "documentVerse");
    },
    "images.faceBiometry"(val) {
      this.validateFace(val);
    },
  },

  methods: {
    async validateIdentity() {
      if (!this.$store.state.logged.status) {
        return;
      }

      const toBase64 = async (file, isPdf = false) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            if (isPdf) {
              resolve(reader.result);
            } else {
              resolve(reader.result.split(",")[1]);
            }
          };
          reader.onerror = (error) => reject(error);
        });
      };

      this.bankValidationLoading = true;

      const data = {
        operatorId: this.$store.state.logged.data.id_banca,
        userId: this.$store.state.userId,
        userCpf: this.userCpf,
        domain: this.$domain(),
        images: [],
      };

      if (this.isFile(this.images.documentFront)) {
        data.images.push({
          label: "documento",
          base64: await toBase64(this.images.documentFront),
          type: this.images.documentFront.type,
        });
      }

      if (this.isFile(this.images.documentVerse)) {
        data.images.push({
          label: "documento_verso",
          base64: await toBase64(this.images.documentVerse),
          type: this.images.documentVerse.type,
        });
      }

      if (this.isFile(this.images.faceBiometry)) {
        data.images.push({
          label: "biometria_face",
          base64: await toBase64(this.images.faceBiometry),
          type: this.images.faceBiometry.type,
        });
      }

      try {
        const response = await axios.post(
          "https://l43izhe67c.execute-api.us-east-2.amazonaws.com/serpro/validate",
          data
        );

        this.$emit("changeDocumentVerificationStatus", 3);

        this.$store.commit("setNotification", {
          msg: response.data.message,
          color: "success",
        });
      } catch (err) {
        if (err.response.data.newStatus) {
          const newStatus = err.response.data.newStatus;
          this.$emit("changeDocumentVerificationStatus", newStatus);
          this.previewImages.documentFront.src = "";
          this.previewImages.documentVerse.src = "";
          this.previewImages.faceBiometry.src = "";
          this.previewImages.documentFront.type = "";
          this.previewImages.documentVerse.type = "";
          this.previewImages.faceBiometry.type = "";
        }

        this.$store.commit("setNotification", {
          msg: err.response.data.message,
          color: "warning",
        });
      } finally {
        this.bankValidationLoading = false;
      }
    },

    openFileSelecter(imageType) {
      const inputEl = this.$refs[imageType].$el;
      inputEl.querySelector(".v-file-input__text").click();
    },

    isFile(input) {
      if ("File" in window && input instanceof File) {
        return true;
      }

      return false;
    },

    validateDocument(file, documentName) {
      if (!file) {
        this.previewImages[documentName].src = "";
        this.previewImages[documentName].type = "";
        this.errors[documentName] = this.$t("required");
        return;
      }

      if (file.type.includes("pdf")) {
        this.updatePdfKey[documentName] += 1;
      }

      this.previewImages[documentName].src = URL.createObjectURL(file);
      this.previewImages[documentName].type = file.type;

      if (!allowedMimes.includes(file.type)) {
        this.errors[documentName] = this.$t(
          "verification.document.allowedTypes"
        );
      } else {
        this.errors[documentName] = "";
      }

      const _this = this;

      if (file.type.includes("pdf")) {
        setTimeout(() => {
          this.$nextTick(() => {
            const el = this.$refs[`previewPdf${documentName}`][0].$el;
            const page = el.querySelector(".page");
            if (page.offsetHeight > page.offsetWidth) {
              _this.errors[documentName] = _this.$t(
                "verification.document.documentNotVertical"
              );
            }
          });
        }, 100);
      } else {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = function() {
          if (_this.isPortrait(img)) {
            _this.errors[documentName] = _this.$t(
              "verification.document.documentNotVertical"
            );
          } else if (img.width < 1000) {
            _this.errors[documentName] = _this.$t(
              "verification.document.minWidth"
            );
          }

          URL.revokeObjectURL(img.src);
        };

        img.src = url;
      }
    },

    validateFace(file) {
      if (!file) {
        this.previewImages.faceBiometry.src = "";
        this.previewImages.faceBiometry.type = "";
        this.errors.faceBiometry = this.$t("required");
        return;
      }

      if (file.type.includes("pdf")) {
        this.updatePdfKey.faceBiometry += 1;
      }

      this.previewImages.faceBiometry.src = URL.createObjectURL(file);
      this.previewImages.faceBiometry.type = file.type;

      if (!allowedMimes.includes(file.type)) {
        this.errors.faceBiometry = this.$t(
          "verification.document.allowedTypes"
        );
      } else {
        this.errors.faceBiometry = "";
      }

      const url = URL.createObjectURL(file);
      const img = new Image();

      const _this = this;

      img.onload = function() {
        if (img.width < 250) {
          _this.errors.faceBiometry = _this.$t(
            "verification.document.faceBiometryMinWidth"
          );
        }

        if (img.height < 250) {
          _this.errors.faceBiometry = _this.$t(
            "verification.document.faceBiometryMinHeight"
          );
        }

        URL.revokeObjectURL(img.src);
      };

      img.src = url;
    },

    isPortrait(img) {
      var w = img.naturalWidth || img.width,
        h = img.naturalHeight || img.height;
      return h > w;
    },
  },
};
</script>

<style>
.v-input .v-label {
  height: fit-content;
}
.v-file-input .v-file-input__text {
  cursor: pointer;
}
</style>
